import React from "react";
import { Link } from "react-router-dom";
import { mapPageSections } from "./experienceApi";
import { sanitizeHtml, sanitizeUrl } from "./sanitize";
import "./ExperiencePageRenderer.css";

/**
 * Phase B Experience Manager page renderer — registry-driven sections.
 */
export function ExperiencePageRenderer({ page, showTitle = true }) {
  if (!page) {
    return null;
  }

  const sections = mapPageSections(page);

  return (
    <div className="phase-b-experience-page" data-slug={page.slug || ""}>
      {showTitle && page.title ? (
        <h1 className="phase-b-experience-title">{page.title}</h1>
      ) : null}
      {sections.map((section) => (
        <ExperienceSection
          key={section.id || `${section.type}-${section.label}`}
          section={section}
        />
      ))}
    </div>
  );
}

function SafeLink({ href, children, className }) {
  const safe = sanitizeUrl(href);
  if (!safe) {
    return <span className={className}>{children}</span>;
  }
  if (safe.startsWith("/")) {
    return (
      <Link className={className} to={safe}>
        {children}
      </Link>
    );
  }
  return (
    <a className={className} href={safe} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function ExperienceSection({ section }) {
  const payload = section.payload || {};
  const type = section.type;

  if (type === "hero") {
    const mediaUrl = payload.media_url || payload.background_image || null;
    const headline = payload.headline || payload.title || null;
    const subheadline = payload.subheadline || payload.subtitle || null;
    const logo = payload.logo || null;
    return (
      <section
        className={`phase-b-section phase-b-hero align-${payload.alignment || "center"}`}
        data-type="hero"
        style={
          mediaUrl
            ? { backgroundImage: `url(${mediaUrl})` }
            : undefined
        }
      >
        <div className="phase-b-hero-inner">
          {logo ? (
            <img
              className="phase-b-hero-logo"
              src={logo}
              alt=""
              style={{
                width: 72,
                height: 72,
                objectFit: "cover",
                borderRadius: "50%",
                marginBottom: "0.75rem",
              }}
            />
          ) : null}
          {headline ? <h2>{headline}</h2> : null}
          {subheadline ? <p>{subheadline}</p> : null}
          {payload.cta_label ? (
            <SafeLink className="phase-b-cta" href={payload.cta_url}>
              {payload.cta_label}
            </SafeLink>
          ) : null}
        </div>
      </section>
    );
  }

  if (type === "rich_text") {
    return (
      <section className="phase-b-section phase-b-rich-text" data-type="rich_text">
        {payload.title ? <h3>{payload.title}</h3> : null}
        <div
          className="phase-b-rich-text-body"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(payload.body_html || payload.html || ""),
          }}
        />
      </section>
    );
  }

  if (type === "cta_banner") {
    return (
      <section
        className={`phase-b-section phase-b-cta-banner style-${payload.style || "primary"}`}
        data-type="cta_banner"
      >
        <h3>{payload.headline || payload.title}</h3>
        {payload.body ? <p>{payload.body}</p> : null}
        {payload.cta_label ? (
          <SafeLink className="phase-b-cta" href={payload.cta_url}>
            {payload.cta_label}
          </SafeLink>
        ) : null}
      </section>
    );
  }

  if (
    type === "featured_carousel" ||
    type === "video_carousel" ||
    type === "category_carousel"
  ) {
    const items = Array.isArray(payload.items) ? payload.items : [];
    return (
      <section className="phase-b-section phase-b-carousel" data-type={type}>
        {payload.title ? <h3>{payload.title}</h3> : null}
        <div className="phase-b-carousel-track">
          {items.length === 0 ? (
            <p className="phase-b-muted">No items configured yet.</p>
          ) : (
            items.map((item, idx) => (
              <div className="phase-b-carousel-card" key={item.id || idx}>
                {item.image ? <img src={item.image} alt="" /> : null}
                <div>{item.title || item.name || "Item"}</div>
              </div>
            ))
          )}
        </div>
      </section>
    );
  }

  if (type === "grid") {
    const items = Array.isArray(payload.items) ? payload.items : [];
    const cols = payload.columns || 3;
    return (
      <section className="phase-b-section phase-b-grid" data-type="grid">
        {payload.title ? <h3>{payload.title}</h3> : null}
        <div
          className="phase-b-grid-inner"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {items.map((item, idx) => (
            <div key={item.id || idx} className="phase-b-grid-item">
              {item.title || item.label || "Item"}
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (type === "sponsor") {
    const sponsors = Array.isArray(payload.sponsors) ? payload.sponsors : [];
    return (
      <section className="phase-b-section phase-b-sponsor" data-type="sponsor">
        {payload.title ? <h3>{payload.title}</h3> : null}
        <div className="phase-b-sponsor-row">
          {sponsors.map((s, idx) => (
            <SafeLink key={idx} href={s.url} className="phase-b-sponsor-item">
              {s.logo_url ? <img src={s.logo_url} alt={s.name || ""} /> : s.name}
            </SafeLink>
          ))}
        </div>
      </section>
    );
  }

  if (type === "newsletter") {
    return (
      <section className="phase-b-section phase-b-newsletter" data-type="newsletter">
        <h3>{payload.headline || "Stay in the loop"}</h3>
        <form
          className="phase-b-newsletter-form"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <input
            type="email"
            placeholder={payload.placeholder || "you@example.com"}
            aria-label="Email"
          />
          <button type="submit">{payload.cta_label || "Subscribe"}</button>
        </form>
      </section>
    );
  }

  if (type === "community_feed" || type === "events") {
    const items = Array.isArray(payload.items) ? payload.items : [];
    return (
      <section className="phase-b-section" data-type={type}>
        {payload.title ? <h3>{payload.title}</h3> : null}
        {items.length === 0 ? (
          <p className="phase-b-muted">Content will appear when configured.</p>
        ) : (
          <ul>
            {items.map((item, idx) => (
              <li key={item.id || idx}>{item.title || item.body || "Item"}</li>
            ))}
          </ul>
        )}
      </section>
    );
  }

  if (type === "custom_html") {
    return (
      <section className="phase-b-section phase-b-custom-html" data-type="custom_html">
        <div
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(payload.html || "") }}
        />
      </section>
    );
  }

  return (
    <section className="phase-b-section phase-b-passthrough" data-type={type}>
      <p>
        [{section.label || type}]
        {!section.implemented ? " — renderer not implemented yet" : ""}
      </p>
    </section>
  );
}

export default ExperiencePageRenderer;
